"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, Gauge, PhoneCall, Pipette, ShieldAlert, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const problemCategories = [{
  title: "Burst & Leaking Pipes",
  description: "Active water line leaks, burst frozen pipes, and sudden ceiling or wall leaks needing immediate shut-off.",
  icon: Droplets,
  badge: "Urgent",
  link: "/problems/",
  emergencyLevel: "High Risk"
}, {
  title: "Clogged Drains & Backups",
  description: "Severe main line clogs, slow draining tubs, overflowing sinks, and hydro-jetting root removals.",
  icon: Wrench,
  badge: "Common",
  link: "/problems/",
  emergencyLevel: "Same Day"
}, {
  title: "Water Heater Failure",
  description: "No hot water, tank leaks, strange noises, pilot light failures, and gas or electric heater repairs.",
  icon: Flame,
  badge: "Priority",
  link: "/water-heater/",
  emergencyLevel: "Urgent"
}, {
  title: "Sewer Line Backups",
  description: "Foul odor, multiple fixture gurgling, wastewater overflow, and camera inspections.",
  icon: ShieldAlert,
  badge: "Hazardous",
  link: "/problems/",
  emergencyLevel: "Immediate"
}, {
  title: "Overflowing Toilets",
  description: "Stubborn toilet clogs, continuous running tanks, weak flushes, and wax ring base leaks.",
  icon: Pipette,
  badge: "Urgent",
  link: "/problems/",
  emergencyLevel: "Fast Dispatch"
}, {
  title: "Low Water Pressure",
  description: "Sudden pressure loss across fixtures, corroded galvanized pipes, or hidden slab leak signs.",
  icon: Gauge,
  badge: "Diagnostic",
  link: "/problems/",
  emergencyLevel: "Inspection"
}];
export default function ProblemCategories() {
  const shouldReduceMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: (index: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        delay: shouldReduceMotion ? 0 : index * 0.08
      }
    })
  };
  return <section id="relatedproblemcategories" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12 md:mb-16">
          <Badge variant="outline" className="mb-3 border-border bg-muted/60 text-foreground">
            Explore Problem Diagnoses
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold font-default tracking-tight mb-4 text-foreground">
            Common Plumbing Emergencies & Issues
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Select an urgent issue below to learn immediate mitigation steps, understand repair processes, or request 24/7 emergency dispatch in Jonesboro, GA.
          </p>
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {problemCategories.map((category, index) => {
          const IconComponent = category.icon;
          return <motion.div key={category.title} data-index={index} custom={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-40px"
          }} variants={cardVariants} className="h-full">
                <Card className="h-full flex flex-col justify-between border-border bg-card hover:shadow-md transition-shadow">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit">
                          <IconComponent className="size-5" />
                        </div>
                        <Badge variant="secondary" className="text-xs font-semibold">
                          {category.badge}
                        </Badge>
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {category.title}
                      </CardTitle>
                      <span className="text-xs font-medium text-muted-foreground">
                        Dispatch: {category.emergencyLevel}
                      </span>
                    </CardHeader>
                    <CardContent className="pt-0 pb-4">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {category.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="outline" className="w-full justify-between group">
                      <Link to={category.link} className="w-full flex items-center justify-between">
                        <span>Troubleshoot & Fix</span>
                        <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Urgent CTA Banner */}
        <div className="rounded-2xl bg-muted/70 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-start sm:items-center gap-4">
            <div className="p-3 rounded-full bg-destructive/10 text-destructive shrink-0">
              <AlertTriangle className="size-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold font-default text-foreground">
                Experiencing an Active Flood or Gas Leak?
              </h3>
              <p className="text-sm text-muted-foreground">
                Do not wait. Shut off your main water valve and call our 24/7 Jonesboro technicians right away.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="w-full sm:w-auto">
              <a href="tel:4708479076" className="flex items-center gap-2">
                <PhoneCall className="size-4" />
                <span>Call Emergency Line</span>
              </a>
            </Button>
            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto">
              <Link to="/contact/">Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}